/**
 * "No active license" gate — shown when an authenticated user has no valid
 * business to sign into (no restaurant, or the restaurant has no place).
 *
 * Both the e-mail/password path and every social-login provider funnel through
 * `RestaurantPlaceChooser` after authentication, so this modal is the single
 * uniform gate: if the user ends up with zero selectable businesses/places we
 * never persist a session — we show this instead of the "Select a business"
 * picker and offer support contact + a way back to the login screen.
 *
 * Premium, brand-consistent, and cross-platform (iOS / Android / React-Native
 * Web): a dark backdrop over a white card with an orange gradient header, a
 * warning glyph, tap-to-contact rows (e-mail / phone / website) and a ghost
 * "back to login" action. Contact links use `Linking`, which resolves to
 * `mailto:` / `tel:` / a new tab on web.
 *
 * @param {object} props
 * @param {boolean} props.visible
 * @param {() => void} props.onClose Return to the login screen (clears the pending session).
 * @param {(key:string, opts?:object)=>string} props.t
 */

import React from 'react';
import { Linking, Modal, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Icon } from 'react-native-elements';
import { LinearGradient } from 'expo-linear-gradient';

const SUPPORT_EMAIL = 'info@happygastro.hu';
const SUPPORT_PHONE = '+3612344321';
const SUPPORT_WEBSITE = 'https://happygastro.hu';

/** Open a contact target, swallowing any "no handler" rejection. */
const openLink = (url) => Linking.openURL(url).catch(() => {});

export default function NoLicenseModal({ visible, onClose, t }) {
    const tr = typeof t === 'function' ? t : (_k, o) => (o && o.defaultValue) || '';

    const contacts = [
        {
            key: 'email',
            icon: 'email',
            label: tr('no_license_contact_email', { defaultValue: 'Email us' }),
            value: SUPPORT_EMAIL,
            onPress: () => openLink(`mailto:${SUPPORT_EMAIL}`),
        },
        {
            key: 'phone',
            icon: 'phone',
            label: tr('no_license_contact_phone', { defaultValue: 'Call us' }),
            value: SUPPORT_PHONE,
            onPress: () => openLink(`tel:${SUPPORT_PHONE.replace(/\s+/g, '')}`),
        },
        {
            key: 'website',
            icon: 'language',
            label: tr('no_license_contact_website', { defaultValue: 'Visit our website' }),
            value: 'happygastro.hu',
            onPress: () => openLink(SUPPORT_WEBSITE),
        },
    ];

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <View style={styles.backdrop}>
                <View style={styles.card}>
                    <LinearGradient
                        colors={['#ff9f1c', '#f97316']}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={styles.header}
                    >
                        <View style={styles.iconBadge}>
                            <Icon name="lock-outline" type="material" size={34} color="#ffffff" />
                        </View>
                        <Text style={styles.kicker}>{tr('no_license_kicker', { defaultValue: 'ACCESS RESTRICTED' })}</Text>
                        <Text style={styles.title}>
                            {tr('no_license_title', { defaultValue: 'No active license' })}
                        </Text>
                    </LinearGradient>

                    <View style={styles.body}>
                        <Text style={styles.message}>
                            {tr('no_license_message', {
                                defaultValue:
                                    'Your account isn’t linked to an active business or license yet, so you can’t use the app right now. Please contact us to activate your access.',
                            })}
                        </Text>

                        <View style={styles.contacts}>
                            {contacts.map((c) => (
                                <TouchableOpacity
                                    key={c.key}
                                    style={styles.contactRow}
                                    activeOpacity={0.85}
                                    onPress={c.onPress}
                                >
                                    <View style={styles.contactIcon}>
                                        <Icon name={c.icon} type="material" size={20} color="#f97316" />
                                    </View>
                                    <View style={styles.contactMeta}>
                                        <Text style={styles.contactLabel}>{c.label}</Text>
                                        <Text style={styles.contactValue} numberOfLines={1}>{c.value}</Text>
                                    </View>
                                    <Icon name="chevron-right" type="material" size={22} color="#CBD5E1" />
                                </TouchableOpacity>
                            ))}
                        </View>

                        <TouchableOpacity style={styles.backBtn} activeOpacity={0.85} onPress={onClose}>
                            <Text style={styles.backText}>
                                {tr('no_license_back', { defaultValue: 'Back to login' })}
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    backdrop: {
        flex: 1,
        backgroundColor: 'rgba(15, 23, 42, 0.6)',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
    },
    card: {
        width: '100%',
        maxWidth: 420,
        backgroundColor: '#ffffff',
        borderRadius: 24,
        overflow: 'hidden',
        ...Platform.select({
            ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 12 }, shadowOpacity: 0.25, shadowRadius: 24 },
            android: { elevation: 12 },
            default: {},
        }),
    },
    header: {
        alignItems: 'center',
        paddingTop: 26,
        paddingBottom: 22,
        paddingHorizontal: 24,
    },
    iconBadge: {
        width: 68,
        height: 68,
        borderRadius: 34,
        backgroundColor: 'rgba(255, 255, 255, 0.22)',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 12,
    },
    kicker: {
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: 11,
        fontWeight: '700',
        letterSpacing: 1.5,
        marginBottom: 4,
    },
    title: { color: '#ffffff', fontSize: 22, fontWeight: '800', textAlign: 'center' },
    body: { padding: 22 },
    message: { fontSize: 14, lineHeight: 21, color: '#475569', textAlign: 'center', marginBottom: 18 },
    contacts: { marginBottom: 16 },
    contactRow: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#F1F5F9',
        backgroundColor: '#F8FAFC',
        borderRadius: 14,
        paddingVertical: 12,
        paddingHorizontal: 14,
        marginBottom: 10,
    },
    contactIcon: {
        width: 38,
        height: 38,
        borderRadius: 19,
        backgroundColor: '#FFF3E9',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
    },
    contactMeta: { flex: 1 },
    contactLabel: { fontSize: 12, color: '#94A3B8', fontWeight: '600' },
    contactValue: { fontSize: 15, color: '#0F172A', fontWeight: '700' },
    backBtn: {
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        borderRadius: 14,
        paddingVertical: 13,
        alignItems: 'center',
    },
    backText: { color: '#475569', fontSize: 15, fontWeight: '700' },
});
